import { Button } from '@/components/common/Button';
import Divider from '@/components/common/Divider';
import PortalEntry from '@/components/common/PortalEntry';
import { getDefaultCurrencyId } from '@/components/form/schema/common';
import {
    PortalFiatWithdrawalFormInput,
    PortalFiatWithdrawalFormValues,
    portalFiatWithdrawalSchema
} from '@/components/form/schema/portalFiatWithdrawalSchema';
import ErrorMessage from '@/components/inputs/ErrorMessage';
import FilterSelect from '@/components/inputs/FilterSelect';
import PortalInputController from '@/components/inputs/PortalInputController';
import PortalNumberInput from '@/components/inputs/PortalNumberInput';
import Select from '@/components/inputs/Select';
import TextArea from '@/components/inputs/TextArea';
import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { ErrorMsg } from '@/types/common';
import useAbortController from '@/utils/hooks/useAbortController';
import useBankOptions from '@/utils/hooks/useBankOptions';
import { useCurrency } from '@/utils/hooks/useCurrency';
import useCurrencyOptions from '@/utils/hooks/useCurrencyOptions';
import useLanguage from '@/utils/hooks/useLanguage';
import { use401Error } from '@/utils/hooks/usePortalAuthentication';
import useToast from '@/utils/hooks/useToast';
import { useSso } from '@/utils/providers/SSOProvider';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebouncedValue } from '@mantine/hooks';
import axios from 'axios';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNumberFormatter } from 'react-aria';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { PortalWithdrawalModalProps } from '.';
import { WhitelistError } from '../PortalInternalTransferModal';

const defaultBankInfoId = { label: 'Select a whitelisted bank account', value: undefined };
const defaultCurrencyId = getDefaultCurrencyId('withdrawal', 'currency');

export const defaultValues: Partial<PortalFiatWithdrawalFormInput> = {
    amount: undefined,
    currencyId: defaultCurrencyId,
    bankInfoId: defaultBankInfoId,
    requestDescription: ''
};

// const steps = [{ label: 'Withdrawal Details' }, { label: 'Confirmation' }];

const FiatWithdrawalForm = (props: PortalWithdrawalModalProps) => {
    const { opened, currency, handlers, onRefresh } = props;

    const lang = useLanguage();
    const { on401Error } = use401Error();
    const [toastError, toastSuccess] = useToast();

    const [tabIndex, setTabIndex] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<ErrorMsg>({ message: '' });

    const form = useForm<PortalFiatWithdrawalFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(portalFiatWithdrawalSchema({}))
    });

    const {
        reset,
        watch,
        setValue,
        setError: setFormError,
        handleSubmit,
        formState: { isValid }
    } = form;

    const [amount, selectedBankInfo, selectedCurrency] = watch(['amount', 'bankInfoId', 'currencyId']);

    const { jwt, foundEntity, balancesApi } = useSso();
    const { isCurrenciesLoading, fiatWithdrawalCcyOptions } = useCurrencyOptions();
    const { bankOptions, bankDetails } = useBankOptions(selectedCurrency, selectedBankInfo);
    const { abortController, setAbortController, onClose } = useAbortController({
        isLoading,
        handlers
    });

    const { data: balances } = balancesApi;

    const hasAccount = useMemo(() => {
        return selectedCurrency?.value && bankOptions?.length;
    }, [selectedCurrency, bankOptions]);

    const currencyConfig = useCurrency(selectedCurrency?.name || '');

    const currentBalance = useMemo(() => {
        return balances?.find((balance: any) => balance.currencyName === selectedCurrency?.label)?.balance || 0;
    }, [balances, selectedCurrency]);

    const [debouncedAmount] = useDebouncedValue(amount, 150);
    const [debouncedBalance] = useDebouncedValue(currentBalance, 150);

    useEffect(() => {
        if (debouncedAmount && debouncedBalance) {
            if (BigNumber(debouncedAmount).isGreaterThan(debouncedBalance)) {
                setFormError('amount', {
                    type: 'manual',
                    message: `Amount exceeded available balance.`
                });
            }
        }
    }, [debouncedAmount, debouncedBalance]);

    const balanceFormatter = useNumberFormatter({
        minimumFractionDigits: currencyConfig?.order_decimals > 1 ? currencyConfig?.order_decimals - 2 : 0,
        maximumFractionDigits: currencyConfig?.order_decimals || 0
    });

    const onSubmit = async (data: PortalFiatWithdrawalFormInput) => {
        setIsLoading(true);
        const dataValidated = data as PortalFiatWithdrawalFormValues;
        try {
            if (foundEntity) {
                const payload = {
                    entityId: foundEntity.entityId,
                    amount: dataValidated.amount || 0,
                    bankInfoId: dataValidated.bankInfoId.value,
                    requestDescription: dataValidated.requestDescription || null,
                    currencyId: dataValidated.currencyId.value
                };
                if (portalURL) {
                    await axios.post(portalAPI('/api/requests/withdrawal/fiat'), payload, {
                        headers: { Authorization: `Bearer ${jwt}` }
                    });
                }
                const title = 'Fiat Withdrawal';
                const body = 'Withdrawal request submitted successfully';
                toastSuccess({ body, title });
                onRefresh?.();
            }
            handlers.close();
        } catch (error: any) {
            on401Error(error, () => {
                logToServer(
                    'error',
                    `Unauthenticated access on /api/requests/withdrawal/fiat. JWT may be invalid or expired.`
                );
            });
            handleApiError(error);
        }
        setIsLoading(false);
    };

    const onError = (e: FieldErrors) => console.log(e);

    const handleApiError = useCallback((error) => {
        setIsLoading(false);
        setError(error?.response?.data || error);
    }, []);

    useEffect(() => {
        if (bankOptions && bankOptions.length > 0 && bankOptions.length < 2) {
            setValue('bankInfoId', bankOptions[0], { shouldValidate: true });
        }
    }, [bankOptions]);

    useEffect(() => {
        if (selectedCurrency?.value) {
            setTabIndex(0);
            let refreshBankInfoId: any = defaultBankInfoId;
            if (bankOptions.length > 0) refreshBankInfoId = bankOptions[0];
            reset({
                ...defaultValues,
                bankInfoId: refreshBankInfoId,
                currencyId: selectedCurrency
            });
        }
    }, [selectedCurrency?.value]);

    useEffect(() => {
        if (selectedCurrency?.value) {
            if (!hasAccount || !bankOptions.length) {
                setError({ message: <WhitelistError type="FIAT" selectedCurrency={selectedCurrency} /> });
            } else {
                if (currentBalance <= 0) {
                    setError({ message: `Insufficient balance for withdrawal` });
                } else {
                    setError({ message: '' });
                }
            }
        }
    }, [bankOptions, currentBalance, selectedCurrency, setValue]);

    const availableBalance = useMemo(() => {
        let balance;
        if (currentBalance) {
            return (
                <div
                    className={cn('flex items-center gap-1', {
                        'text-brand-green-light': currentBalance > 0,
                        'text-brand-red-light': currentBalance < 0
                    })}>
                    {balanceFormatter.format(currentBalance)}
                </div>
            );
        } else balance = lang.portal.noAvailableBalance;

        return selectedCurrency?.name ? balance : lang.portal.selectAWithdrawalCurrency;
    }, [lang, currentBalance, balanceFormatter, selectedCurrency, currencyConfig]);

    useEffect(() => {
        const prefillCcy = fiatWithdrawalCcyOptions.find((option) => option.name === currency);
        if (prefillCcy) setValue('currencyId', prefillCcy, { shouldValidate: true });
    }, []);

    useEffect(() => {
        if (opened) {
            balancesApi.mutate();
            setAbortController(new AbortController());
        } else abortController.abort();

        return () => abortController.abort();
    }, [opened]);

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit, onError)} className="relative h-full w-full text-brand-text">
                <div className="p-2 sm:p-4">
                    {/* <FormStepper.Main activeStep={tabIndex} onStep={(step) => setTabIndex(step)}>
                        {steps.map(({ label }, index) => (
                            <FormStepper.Step key={label} step={index} label={label} />
                        ))}
                    </FormStepper.Main> */}
                    <div className="flex flex-col gap-4 h-[500px] overflow-y-auto text-sm">
                        <fieldset className="space-y-3">
                            <PortalEntry
                                label={lang.portal.availableBalance}
                                value={availableBalance}
                                className={cn('', {
                                    ['text-brand-neutral-200 text-sm']:
                                        !selectedCurrency?.name || availableBalance === 'No available balance'
                                })}
                                valueOnly
                            />
                        </fieldset>
                        <fieldset className="space-y-3">
                            <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                <div className="flex-1">
                                    <PortalInputController
                                        name="currencyId"
                                        label={lang.lite.currency}
                                        asyncInput={!jwt || isCurrenciesLoading}
                                        placeholder="Currency"
                                        required>
                                        <FilterSelect
                                            options={fiatWithdrawalCcyOptions}
                                            enableCurrencyIcon
                                            enableSorting
                                        />
                                    </PortalInputController>
                                </div>
                                <div className="flex-1">
                                    <PortalInputController
                                        name="amount"
                                        label={lang.portal.amountToWithdraw}
                                        placeholder="0.00"
                                        required>
                                        <PortalNumberInput currency={selectedCurrency?.name || ''} />
                                    </PortalInputController>
                                </div>
                            </div>
                        </fieldset>
                        <fieldset className="space-y-3">
                            <legend className="text-xs text-brand-neutral-500">{lang.portal.bankDetails}</legend>
                            <PortalInputController name="bankInfoId" label={lang.blotters.bankAccount} required>
                                <Select options={bankOptions} />
                            </PortalInputController>
                            {bankDetails && (
                                <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                    <div className="flex-1">
                                        <PortalEntry
                                            label={lang.portal.bankName}
                                            value={bankDetails.bankName}
                                            disabled
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <PortalEntry
                                            label={`${lang.portal.accountNumber} / ${lang.portal.ibanCode}`}
                                            value={bankDetails.accountNumber || bankDetails.ibanCode}
                                            disabled
                                        />
                                    </div>
                                </div>
                            )}
                        </fieldset>
                        <PortalInputController
                            name="requestDescription"
                            label={lang.portal.requestDescription}
                            placeholder={lang.portal.requestDescription}>
                            <TextArea rows={2} />
                        </PortalInputController>
                        <ErrorMessage error={error} />
                    </div>
                </div>
                <Divider neutral />
                <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                    <button
                        type="button"
                        className="w-auto rounded-md p-2 px-4 bg-brand-neutral-600 hover:bg-brand-neutral-500"
                        onClick={() => {
                            if (!isLoading) onClose();
                        }}>
                        {lang.common.cancel}
                    </button>
                    <Button
                        type="button"
                        className="w-auto rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                        onClick={() => {
                            if (!isLoading) handleSubmit(onSubmit, onError)();
                        }}
                        disabled={!isValid || currentBalance <= 0}
                        isLoading={isLoading}>
                        {lang.portal.submitRequest}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default FiatWithdrawalForm;
